import { useContext } from "react";
import {
  createUserWithEmailAndPassword,
  signInAnonymously,
  signInWithEmailAndPassword,
  updateEmail,
  updateProfile,
  UserCredential,
  confirmPasswordReset as authConfirmPasswordReset,
  getRedirectResult,
  onAuthStateChanged,
  signOut as authSignOut,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { push } from "connected-react-router";
import useUserProfile from "modules/userProfile/hooks/useUserProfile";
import companySettingsActions from "modules/companySettings/redux/actions";
import { parseURL } from "helpers/urlHelper";
import { getRemoHosts } from "config";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import retry from "async-retry";
import { useI18n } from "i18n";
import { selectCurrentEventId } from "modules/event/selectors";
import { resetUsersInEvent } from "modules/event/usersInEvent/actions";
import { IS_IN_GOOGLE_AUTH } from "common";
import { clearCoupon } from "modules/company/companyCoupon/companyCouponSlice";
import { IUser } from "modules/app/types";
import { getUserName } from "modules/userProfile";
import { setGlobalContext, Errors, trackError } from "modules/monitoring";
import { useUserSession } from "modules/eventUserPresence/hooks/userSession";
import { selectCompanyId } from "modules/company/redux/selectors";
import logger from "logging/logger";
import { useNeedHelp } from "modules/helpChat";
import { queryClient } from "services/apiService/queryClient";
import { addDialogNotification } from "modules/dialogNotification/redux/dialogNotificationSlice";
import { firebaseAuth } from "services/firebaseService/firebaseConfig";
import { PASSWORD_DEFAULTS } from "../constants";
import useAuthActions from "./useAuthActions";
import { onBoard } from "../../appSetting/redux/actions";
import { reset } from "../../company/redux/slice";
import { resetEvents } from "../../event/eventSlice";
import { selectAuth } from "../redux/selectors";
import {
  resetAuth,
  setCheckStatus,
  updateTokenSyncStatus,
} from "../redux/authSlice";
import useAuthFlags from "./useAuthFlags";
import { toggleSSOEventLinkSource } from "../utils/ssoUtils";
import { ISignUpParams } from "../types";

const useAuth = () => {
  const { t } = useI18n(["auth", "errorPage", "server"]);
  const dispatch = useAppDispatch();
  const { addErrorNotification, addSuccessNotification } =
    useNotificationActions();
  const { isLoading, user, isEventHelper } = useSelector(selectAuth);

  const {
    isAuthenticated,
    isAnonymous,
    isActualAdmin,
    isActualManager,
    isCompanyOwnerInOwnEvent,
  } = useAuthFlags();

  const currentEventId = useSelector(selectCurrentEventId);
  const companyId = useSelector(selectCompanyId);
  const {
    createProfile,
    getUserByEmail,
    syncUseridInClaims,
    createAnonymousUser,
  } = useUserProfile();
  const { callApi } = useNeedHelp();
  const { track, reset: resetAnalytics } = useContext(TRACKING_CONTEXT);
  const { completedCheckingAuth, setAuthUser, retrieveUserProfile } =
    useAuthActions();

  const { clearCurrentSession } = useUserSession();

  const signIn = async (
    email: string,
    password: string,
  ): Promise<UserCredential | void> => {
    dispatch(setCheckStatus(true));
    try {
      const userCheckResult = await getUserByEmail(email);

      if (userCheckResult && userCheckResult.google) {
        throw new Error(t("auth:please.login.google"));
      }

      if (userCheckResult && userCheckResult.saml) {
        dispatch(
          addDialogNotification({
            hideCloseButton: true,
            message: t("auth:email.associated.sso"),
            title: t("auth:logged.sso.earlier"),
            hideDismissButton: true,
            onConfirm: () => {
              push("/");
              completedCheckingAuth();
            },
          }),
        );

        return;
      }
      toggleSSOEventLinkSource();
      const result = await signInWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );

      if (result?.user) {
        track(Events.LOGIN_SUCCESS);
      }

      // eslint-disable-next-line consistent-return
      return result;
    } catch (error) {
      const firebaseError = error as FirebaseError;

      logger.error(`[useAuth][signIn] email: ${email} error`, firebaseError);
      const errorMessage = "server:email.password.not.found";
      const errorCode = firebaseError.code;

      if (
        errorCode === "auth/wrong-password" ||
        errorCode === "auth/user-not-found"
      ) {
        addErrorNotification({
          message: t(errorMessage),
          position: "tc",
          autoDismiss: 5,
        });
      } else {
        addErrorNotification({
          message: t(firebaseError.message),
          position: "tc",
          autoDismiss: 5,
        });
      }

      completedCheckingAuth();
    }
  };

  const signUp = async (
    userInfo: ISignUpParams,
  ): Promise<UserCredential | undefined> => {
    const { email, password, firstName, lastName, ...additionalInfo } =
      userInfo;

    if (password.length > PASSWORD_DEFAULTS.MAX_LENGTH) {
      addErrorNotification({
        message: t("auth:max.password.limit", {
          length: PASSWORD_DEFAULTS.MAX_LENGTH,
        }),
        position: "tc",
        autoDismiss: 5,
      });

      return;
    }

    dispatch(setCheckStatus(true));
    try {
      await createProfile({
        email,
        firstName,
        lastName,
        ...additionalInfo,
      });

      if (firebaseAuth.currentUser) {
        await updateProfile(firebaseAuth.currentUser, {
          displayName: `${firstName} ${lastName}`,
        });
      }

      toggleSSOEventLinkSource();
      const userData = await createUserWithEmailAndPassword(
        firebaseAuth,
        email,
        password,
      );

      // eslint-disable-next-line consistent-return
      return userData;
    } catch (error) {
      const firebaseError = error as FirebaseError;

      const errorCode = firebaseError.code;
      const errorMessage = t(firebaseError.message);

      if (errorCode === "auth/weak-password") {
        addErrorNotification({
          message: t("auth:min.password.limit", {
            length: PASSWORD_DEFAULTS.MIN_LENGTH,
          }),
          position: "tc",
          autoDismiss: 5,
        });
      } else {
        addErrorNotification({
          message: errorMessage,
          position: "tc",
          autoDismiss: 5,
        });
      }
      completedCheckingAuth();
    }
  };

  const anonymousUserSignUp = async (name: string) => {
    try {
      const userData = await createAnonymousUser({ name });
      if (!userData) {
        throw new Error(t("auth:anonymous.login.error"));
      }
      await signInAnonymously(firebaseAuth);
      if (firebaseAuth.currentUser) {
        await updateEmail(firebaseAuth.currentUser, userData.email);
        await updateProfile(firebaseAuth.currentUser, { displayName: name });
      }
      setAuthUser(userData);
      track(Events.USER_LOGGED_IN_ANONYMOUSLY, {
        userId: userData?.id,
        name: getUserName(userData),
        email: userData.email,
      });
      track(Events.LOGIN_SUCCESS_V2, {
        type: "new",
      });
    } catch (error) {
      const firebaseError = error as FirebaseError;

      const errorMessage = t(firebaseError.message);

      addErrorNotification({
        message: errorMessage,
        position: "tc",
        autoDismiss: 5,
      });
      trackError(errorMessage, {
        label: Errors.ANONYMOUS_LOG_IN,
      });
    }
  };

  const signOut = async () => {
    if (currentEventId && user) {
      await clearCurrentSession({ eventId: currentEventId });
    }

    if (companyId) {
      track(
        Events.SIGNED_OUT_B2B,
        {},
        {
          groupId: companyId,
        },
      );
    }

    window.localStorage.removeItem(IS_IN_GOOGLE_AUTH);

    // Need to refactor
    callApi("logout");
    dispatch(resetEvents());
    dispatch(resetUsersInEvent());
    dispatch(reset());
    dispatch(clearCoupon());
    dispatch(companySettingsActions.resetCompanySettings());
    resetAnalytics();
    await authSignOut(firebaseAuth);
    dispatch(resetAuth());
    dispatch(onBoard(false));

    queryClient.resetQueries();
  };

  const confirmPasswordReset = (
    code: string,
    newPassword: string,
    continueURL?: string,
  ): Promise<void> | undefined => {
    if (newPassword.length > PASSWORD_DEFAULTS.MAX_LENGTH) {
      addErrorNotification({
        message: t("auth:max.password.limit", {
          length: PASSWORD_DEFAULTS.MAX_LENGTH,
        }),
        position: "tc",
        autoDismiss: 5,
      });

      return undefined;
    }

    dispatch(setCheckStatus(true));

    return (
      authConfirmPasswordReset(firebaseAuth, code, newPassword)
        // eslint-disable-next-line promise/prefer-await-to-then
        .then(() => {
          addSuccessNotification({
            message: t("auth:password.change.success"),
            position: "tc",
            autoDismiss: 5,
          });
          const isRemoDomain = getRemoHosts().includes(window.location.host);

          // eslint-disable-next-line promise/always-return
          if (continueURL && isRemoDomain) {
            const { path, search } = parseURL(continueURL);

            dispatch(push(`${path}${search}`));
          } else {
            dispatch(push("/signin"));
          }
          completedCheckingAuth();
        })
        // eslint-disable-next-line promise/prefer-await-to-then
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = t(error.message);

          if (errorCode === "auth/weak-password") {
            addErrorNotification({
              message: t("auth:min.password.limit", {
                length: PASSWORD_DEFAULTS.MIN_LENGTH,
              }),
              position: "tc",
              autoDismiss: 5,
            });
          } else if (errorCode === "auth/expired-action-code") {
            addErrorNotification({
              message: t("auth:reset.password.code.expired"),
              position: "tc",
              autoDismiss: 5,
            });
          } else if (errorCode === "auth/invalid-action-code") {
            addErrorNotification({
              message: t("auth:password.code.invalid"),
              position: "tc",
              autoDismiss: 5,
            });
          } else if (errorCode === "auth/user-disabled") {
            addErrorNotification({
              message: t("auth:user.disabled"),
              position: "tc",
              autoDismiss: 5,
            });
          } else if (errorCode === "auth/user-not-found") {
            addErrorNotification({
              message: t("auth:user.not.found"),
              position: "tc",
              autoDismiss: 5,
            });
          } else {
            addErrorNotification({
              message: errorMessage,
              position: "tc",
              autoDismiss: 5,
            });
          }
          completedCheckingAuth();
        })
    );
  };

  const isNewTokenNeeded = async (userInfo: Partial<IUser>) => {
    const { currentUser } = firebaseAuth;

    if (!currentUser) {
      return false;
    }

    const token = await currentUser.getIdTokenResult();
    const userId = token?.claims?.userId;

    if (userId && userId === (userInfo && userInfo.id)) {
      dispatch(updateTokenSyncStatus(true));

      return false;
    }

    return true;
  };

  const checkAndSyncUseridInToken = async () => {
    retry(
      async () => {
        const { currentUser } = firebaseAuth;
        const userInfo = await syncUseridInClaims();
        const newTokenNeeded = await isNewTokenNeeded(userInfo);

        if (newTokenNeeded) {
          logger.warn("[useAuth][syncUseridInToken] userid not exist in token");
          await currentUser?.getIdToken(true);
          throw new Error("USERID_NOT_EXIST_IN_CLAIM");
        }
      },
      {
        retries: 5,
        minTimeout: 0,
      },
      // eslint-disable-next-line promise/prefer-await-to-then
    ).catch((err) => {
      logger.error(
        `[useAuth][syncUseridInToken] unable to set userid ${err.message}`,
      );
    });
  };

  const subscribe = () => {
    dispatch(setCheckStatus(true));
    getRedirectResult(firebaseAuth)
      // eslint-disable-next-line promise/prefer-await-to-then
      .then(async (result) => {
        // eslint-disable-next-line promise/always-return
        if (result && result.user) {
          await retrieveUserProfile();
          track(Events.LOGIN_SUCCESS);
        }
        completedCheckingAuth();
      })
      // eslint-disable-next-line promise/prefer-await-to-then
      .catch((e) => {
        switch (e.code) {
          case "auth/account-exists-with-different-credential":
            addErrorNotification({
              message: e.message,
              autoDismiss: 10,
            });
            break;
          default:
            logger.error(`[useAuth][subscribe] Error code: ${e?.code}`);
            break;
        }

        completedCheckingAuth();
      });

    const unsubscribe = onAuthStateChanged(
      firebaseAuth,
      async (changedUser) => {
        setGlobalContext("loggedIn", user !== null);
        if (changedUser) {
          await checkAndSyncUseridInToken();

          if (!changedUser.isAnonymous) {
            if (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore firebase compat does not have updated types for metadata
              Date.now() - Number(changedUser.metadata.lastLoginAt ?? 0) <
              5000
            ) {
              track(Events.LOGIN_SUCCESS_V2, {
                type:
                  changedUser.metadata.creationTime ===
                  changedUser.metadata.lastSignInTime
                    ? "new"
                    : "existing",
              });
            }

            await retrieveUserProfile();
            completedCheckingAuth();
          } else if (changedUser.displayName) {
            await retrieveUserProfile();
          }
        }
      },
    );

    return unsubscribe;
  };

  return {
    isLoading,
    setAuthUser,
    signIn,
    signUp,
    signOut,
    confirmPasswordReset,
    isAuthenticated,
    isAnonymous,
    subscribe,
    isAdmin: isActualAdmin,
    isManager: isActualManager,
    isEventHelper,
    isCompanyOwnerInOwnEvent,
    anonymousUserSignUp,
  };
};

export default useAuth;
